// frontend/src/lib/ingressChatClient.ts
import { apiJson, ApiError } from "./api";

// NOTE: Chat must respect ?tenant_id=... (shareable) like the Ingress Runner does.
export const ROOT_TENANT_UUID = "00000000-0000-0000-0000-000000000001";
export const DEFAULT_TENANT_LABEL = "root";

function isUuidLike(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    (s || "").trim()
  );
}

function tenantUuidFromLocation(): string {
  try {
    const sp = new URLSearchParams(window.location.search || "");
    const tid = String(sp.get("tenant_id") || "").trim();
    if (tid && isUuidLike(tid)) return tid;
  } catch {
    // ignore
  }
  return ROOT_TENANT_UUID;
}

// Dynamic per-page-load tenant UUID used by this client
export const TENANT_UUID = tenantUuidFromLocation();

// This is only used for headers where needed (most ingress endpoints ignore it / don't trust it)
export const TENANT_LABEL = DEFAULT_TENANT_LABEL;

export type IngressAppPublic = {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  is_published: boolean;
  auth_mode?: string | null;
  input_json_schema?: any;
  ui_schema?: any;
  output_rules?: any;
};

export type IngressThread = {
  id: string;
  tenant_id?: string;
  app_id?: string;
  title?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type IngressMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  content_json?: any | null;
  request_id?: string | null;
  created_at?: string;
};

export type ThreadGetResp =
  | { thread?: IngressThread; messages?: IngressMessage[] }
  | { messages?: IngressMessage[] }
  | any;

export type SubmitReqBody = {
  thread_id?: string;
  message?: string;
  inputs: Record<string, any>;
  ignore_memory?: boolean;
};

export type SubmitResp = {
  request_id: string;
  status: string;
};

export type RequestStatus = {
  id: string;
  status: "queued" | "running" | "succeeded" | "failed" | string;
  run_id?: string | null;
  error?: string | null;
  created_at?: string;
  started_at?: string | null;
  finished_at?: string | null;
};

export type RequestOutput = any;

export type AdminIngressRequestDetail = any;

// ---- helpers ----

export function appKeyStorageKey(slug: string, tenantId: string) {
  return `ingress.chat.api_key.${tenantId}.${slug}`;
}
export function appThreadsStorageKey(slug: string, tenantId: string) {
  return `ingress.chat.threads.${tenantId}.${slug}`;
}
export function appSelectedThreadStorageKey(slug: string, tenantId: string) {
  return `ingress.chat.selected_thread.${tenantId}.${slug}`;
}
export function appDraftActionStorageKey(slug: string, tenantId: string) {
  return `ingress.chat.draft_action.${tenantId}.${slug}`;
}

export function getSessionJson<T>(key: string, fallback: T): T {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}
export function setSessionJson(key: string, value: any) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}
export function setSessionStr(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value);
  } catch {}
}
export function getSessionStr(key: string): string {
  try {
    return sessionStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

export function buildIngressKeyHeaders(apiKey: string | null | undefined): Record<string, string> {
  const k = (apiKey || "").trim();
  if (!k) return {};
  return { "X-Ingress-Key": k };
}

function withHeaders(headers?: Record<string, string>) {
  return {
    headers: {
      "Content-Type": "application/json",
      ...(headers || {}),
    },
  };
}

// ---- client ----
export async function fetchIngressAppBySlug(slug: string): Promise<IngressAppPublic> {
  return apiJson<IngressAppPublic>(`/v1/ingress/apps/${encodeURIComponent(slug)}?tenant_id=${TENANT_UUID}`);
}

export async function createThread(slug: string, title?: string, apiKey?: string): Promise<IngressThread> {
  const headers = buildIngressKeyHeaders(apiKey);
  const body = { title: (title || "").trim() || "New chat" };
  return apiJson<IngressThread>(`/v1/ingress/apps/${encodeURIComponent(slug)}/threads?tenant_id=${TENANT_UUID}`, {
    method: "POST",
    ...withHeaders(headers),
    body: JSON.stringify(body),
  });
}

/**
 * Optional: list threads from backend if endpoint exists.
 * If 404, caller should fallback to local sessionStorage list.
 */
export async function tryListThreads(slug: string, apiKey?: string): Promise<IngressThread[] | null> {
  const headers = buildIngressKeyHeaders(apiKey);
  try {
    const resp = await apiJson<any>(`/v1/ingress/apps/${encodeURIComponent(slug)}/threads?tenant_id=${TENANT_UUID}`, {
      method: "GET",
      headers: { ...(headers || {}) },
    } as any);
    if (Array.isArray(resp)) return resp as IngressThread[];
    if (Array.isArray(resp?.threads)) return resp.threads as IngressThread[];
    return null;
  } catch (e: any) {
    // If endpoint doesn't exist (404) or forbidden etc., return null to fallback
    const msg = e instanceof ApiError ? e.message : String(e);
    if (msg.includes("404") || msg.toLowerCase().includes("not found")) return null;
    return null;
  }
}

export async function getThread(threadId: string, apiKey?: string): Promise<{ thread?: IngressThread; messages: IngressMessage[] }> {
  const headers = buildIngressKeyHeaders(apiKey);
  const resp = await apiJson<ThreadGetResp>(`/v1/ingress/threads/${encodeURIComponent(threadId)}?tenant_id=${TENANT_UUID}`, {
    method: "GET",
    headers: { ...(headers || {}) },
  } as any);

  const messages: IngressMessage[] = Array.isArray(resp?.messages) ? resp.messages : [];
  const thread: IngressThread | undefined = resp?.thread && typeof resp.thread === "object" ? resp.thread : undefined;
  return { thread, messages };
}

export async function submitTurn(slug: string, body: SubmitReqBody, apiKey?: string): Promise<SubmitResp> {
  const headers = buildIngressKeyHeaders(apiKey);
  return apiJson<SubmitResp>(`/v1/ingress/apps/${encodeURIComponent(slug)}/requests?tenant_id=${TENANT_UUID}`, {
    method: "POST",
    ...withHeaders(headers),
    body: JSON.stringify(body),
  });
}

export async function getRequestStatus(requestId: string, apiKey?: string): Promise<RequestStatus> {
  const headers = buildIngressKeyHeaders(apiKey);
  return apiJson<RequestStatus>(`/v1/ingress/requests/${encodeURIComponent(requestId)}?tenant_id=${TENANT_UUID}`, {
    method: "GET",
    headers: { ...(headers || {}) },
  } as any);
}

export async function getRequestOutput(requestId: string, apiKey?: string): Promise<RequestOutput> {
  const headers = buildIngressKeyHeaders(apiKey);
  return apiJson<RequestOutput>(`/v1/ingress/requests/${encodeURIComponent(requestId)}/output?tenant_id=${TENANT_UUID}`, {
    method: "GET",
    headers: { ...(headers || {}) },
  } as any);
}

/**
 * Best-effort: try to fetch admin request detail to show memory audit.
 * This is OPTIONAL; chat runner still works without it.
 */
export async function tryGetAdminRequestDetail(requestId: string): Promise<AdminIngressRequestDetail | null> {
  try {
    // Depending on your backend, this may exist as:
    // - /v1/admin/ingress/requests/{id}
    // - or a list endpoint filtered by id
    const resp = await apiJson<any>(`/v1/admin/ingress/requests/${encodeURIComponent(requestId)}?tenant_id=${TENANT_UUID}`);
    return resp;
  } catch {
    return null;
  }
}

export async function pollUntilTerminal(
  requestId: string,
  apiKey: string | undefined,
  opts?: { intervalMs?: number; maxAttempts?: number; onTick?: (st: RequestStatus) => void }
): Promise<RequestStatus> {
  const intervalMs = opts?.intervalMs ?? 500;
  const maxAttempts = opts?.maxAttempts ?? 80;

  let last: RequestStatus | null = null;
  for (let i = 0; i < maxAttempts; i++) {
    const st = await getRequestStatus(requestId, apiKey);
    last = st;
    opts?.onTick?.(st);
    if (st.status === "succeeded" || st.status === "failed") return st;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return last || { id: requestId, status: "unknown" };
}